/**
 * Amplitude Analytics Integration
 *
 * Handles event tracking, user identification, and UTM parameter capture
 * Uses Amplitude SDK for analytics with first-touch attribution
 *
 * Controlled by REACT_APP_AMPLITUDE_API_KEY env variable (defaults to undefined)
 */

import * as amplitude from '@amplitude/analytics-browser'
import { Identify } from '@amplitude/analytics-browser'
import { BrowserClient, BrowserOptions } from '@amplitude/analytics-browser/lib/esm/types'
import { getAttribution } from './attribution'
import { STORAGE_KEYS } from './constants'
import { classifyFunnelEvent, FunnelEvent, resolveSurface } from './funnel'
import { emitGAEvent, identifyGAUser, resetGAUser } from './google-analytics'
import { emitGoogleAdsFunnelEvent } from './googleAdsPixel'
import { emitMetaFunnelEvent } from './metaPixel'
import { PropertyValue, UTMParams } from './types'
import { clearUserPropertiesCache, sendUserProperties } from './userProperties'

/**
 * Global Amplitude instance - set once during app initialization
 * Protected against race conditions via explicit setAsGlobal flag
 */
let globalInstance: BrowserClient | undefined = undefined
const lastIdentifySignatureByInstance = new WeakMap<BrowserClient, string>()
const lastUserIdByInstance = new WeakMap<BrowserClient, string>()

const getUTMFromURL = (): UTMParams => Object.fromEntries(new URLSearchParams(window.location.search))

const hasProperties = (properties: UTMParams): boolean => Object.keys(properties).length > 0

const getSortedProperties = (properties: UTMParams): UTMParams =>
    Object.fromEntries(Object.entries(properties).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)))

const getIdentifySignature = (instance: BrowserClient, properties: UTMParams): string =>
    JSON.stringify({
        userId: lastUserIdByInstance.get(instance) || null,
        properties: getSortedProperties(properties),
    })

const sendSetOnceIdentify = (properties: UTMParams, instance: BrowserClient): void => {
    const signature = getIdentifySignature(instance, properties)
    if (lastIdentifySignatureByInstance.get(instance) === signature) return
    lastIdentifySignatureByInstance.set(instance, signature)

    const identifyObj = new Identify()
    Object.entries(properties).forEach(([key, value]) => {
        identifyObj.setOnce(key, value)
    })
    instance.identify(identifyObj)
}

const getStoredUTM = (): UTMParams => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.FIRST_UTM)
        return stored ? JSON.parse(stored) : {}
    } catch (error) {
        console.error('Error parsing stored UTM:', error)
        return {}
    }
}

const storeUTM = (utm: UTMParams): void => {
    try {
        localStorage.setItem(STORAGE_KEYS.FIRST_UTM, JSON.stringify(utm))
    } catch (error) {
        console.error('Error storing UTM:', error)
    }
}

export interface InitAmplitudeOptions extends BrowserOptions {
    /**
     * If true, sets this instance as the global instance.
     * Only the first call with setAsGlobal=true will set the global instance.
     * Subsequent calls with setAsGlobal=true will be ignored (no-op for global assignment).
     */
    setAsGlobal?: boolean
}

/**
 * Initializes Amplitude analytics SDK
 * Sets up UTM capture on route changes
 *
 * @param key - Amplitude API key
 * @param options - Configuration options including setAsGlobal flag
 * @returns The created BrowserClient instance
 *
 * @example
 * // Initialize as global instance (call once at app startup)
 * initAmplitude('your-api-key', { setAsGlobal: true })
 *
 * // Create additional instance without affecting global
 * const secondaryInstance = initAmplitude('secondary-key')
 */
export const initAmplitude = (key: string, options?: InitAmplitudeOptions): BrowserClient => {
    const instance = amplitude.createInstance()

    instance.init(key, {
        autocapture: {
            sessions: false,
            attribution: false,
            pageViews: false,
        },
        flushIntervalMillis: 15000,
        flushQueueSize: 30,
        flushMaxRetries: 5,
        ...options,
    })

    // Set as global instance only if requested AND global is not already set
    if (options?.setAsGlobal && !globalInstance) {
        globalInstance = instance
    }

    // Events queue for up to flushIntervalMillis before sending. If the tab closes
    // first (short-lived pages like /extension/uninstall), queued events are lost —
    // switch to sendBeacon and flush so the browser delivers them after unload.
    window.addEventListener('pagehide', () => {
        instance.setTransport('beacon')
        instance.flush()
    })

    window.addEventListener('popstate', () => {
        const urlUTM = getUTMFromURL()
        if (hasProperties(urlUTM)) captureUTM(urlUTM, instance)
    })

    const originalPushState = history.pushState.bind(history)
    const originalReplaceState = history.replaceState.bind(history)

    history.pushState = (...args) => {
        originalPushState(...args)
        const urlUTM = getUTMFromURL()
        if (hasProperties(urlUTM)) captureUTM(urlUTM, instance)
    }

    history.replaceState = (...args) => {
        originalReplaceState(...args)
        const urlUTM = getUTMFromURL()
        if (hasProperties(urlUTM)) captureUTM(urlUTM, instance)
    }

    const urlUTM = getUTMFromURL()
    if (hasProperties(urlUTM)) captureUTM(urlUTM, instance)

    return instance
}

/**
 * Captures UTM parameters and sets as user properties in Amplitude
 * Merges with stored UTM, stored values take precedence (first-touch attribution)
 * Silently no-ops if no instance provided and global is not initialized
 */
const captureUTM = (userProperties?: UTMParams, instance: BrowserClient | undefined = globalInstance): void => {
    if (!instance) return
    const storedUTM = getStoredUTM()
    const mergedUTM = { ...userProperties, ...storedUTM }

    if (!hasProperties(mergedUTM)) return

    storeUTM(mergedUTM)
    sendSetOnceIdentify(mergedUTM, instance)
}

/**
 * When a raw signup event fires, ALSO emit the unified surface-tagged funnel event
 * (`signup_started` / `signup_completed`) to Amplitude+GA and fan out to the Meta pixel
 * (Lead / CompleteRegistration). Keeps the many legacy event names intact while giving
 * one clean, surface-segmentable funnel across web + extension + native-OAuth callbacks.
 */
// PII keys that must NEVER be forwarded to GA4 / Meta as event properties (GA4 MP ToS
// prohibits PII; Meta must receive hashed identifiers only, which we don't do client-side).
// Some legacy signup sites still pass `email` — this is the safety net that strips it here,
// at the single funnel chokepoint, regardless of what any call site sends.
const PII_KEYS = new Set(['email', 'phone', 'name', 'first_name', 'last_name'])

const stripPII = (props: Record<string, PropertyValue>): Record<string, PropertyValue> =>
    Object.fromEntries(Object.entries(props).filter(([k]) => !PII_KEYS.has(k)))

const fanOutFunnelEvent = (rawEventName: string, properties: Record<string, PropertyValue> | undefined, instance: BrowserClient): void => {
    const funnelEvent = classifyFunnelEvent(rawEventName)
    if (!funnelEvent) return

    const surface = resolveSurface(properties)
    const method = typeof properties?.method === 'string' ? properties.method : undefined

    // Attach the captured first-touch attribution (utm + gclid/fbclid/msclkid) so every
    // funnel event answers "which platform did this come from" without editing each site.
    const attribution = getAttribution() as Record<string, PropertyValue>

    // Unified funnel event to Amplitude + GA (skipGA=false so GA gets it too). Emitting
    // by a fixed name means it won't re-classify — no recursion. PII stripped before send.
    const funnelProps: Record<string, PropertyValue> = stripPII({ surface, ...(method ? { method } : {}), ...attribution })
    try {
        instance.track(funnelEvent, { ...funnelProps, source: 'web' })
        emitGAEvent(funnelEvent, funnelProps)
    } catch {
        /* funnel mirror must never break the app */
    }

    // Meta standard event (only SIGNUP_* map; PageView stays in useMetaPixel). No PII passed —
    // the eventID is derived from non-identifying inputs (see buildMetaEventId).
    emitMetaFunnelEvent(funnelEvent as FunnelEvent, surface, { method })

    // Google Ads conversion pixel (SIGNUP_COMPLETED only — see googleAdsPixel.ts). Covers every
    // surface uniformly instead of relying on one specific onboarding screen to render.
    emitGoogleAdsFunnelEvent(funnelEvent as FunnelEvent)
}

/**
 * Tracks an analytics event
 * Uses global instance by default, or pass explicit instance
 * Silently no-ops if no instance available
 */
export const track = (
    eventName: string,
    properties?: Record<string, PropertyValue>,
    instance: BrowserClient | undefined = globalInstance,
    options?: { skipGA?: boolean }
): void => {
    if (!instance) return
    const urlUTM = getUTMFromURL()
    if (hasProperties(urlUTM)) captureUTM(urlUTM, instance)

    instance.track(eventName, {
        ...urlUTM,
        ...properties,
        source: 'web',
    })

    if (!options?.skipGA) {
        try {
            emitGAEvent(eventName, { ...urlUTM, ...properties })
        } catch {
            /* GA must never break the app */
        }
    }

    // Fan out signup events to the unified funnel + Meta. Guarded internally so only
    // signup_started/completed-class events do anything; everything else is a no-op.
    fanOutFunnelEvent(eventName, properties, instance)
}

/**
 * Immediately sends all queued events instead of waiting for the flush interval.
 * Call right after tracking on pages the user is likely to close within
 * flushIntervalMillis (e.g. the extension uninstall landing page).
 * Silently no-ops if no instance available.
 */
export const flush = (instance: BrowserClient | undefined = globalInstance): void => {
    if (!instance) return
    instance.flush()
}

/**
 * Identifies a user in Amplitude
 * Uses global instance by default, or pass explicit instance
 * Silently no-ops if no instance available
 */
export const identify = (userId: string, userProperties?: Record<string, PropertyValue>, instance: BrowserClient | undefined = globalInstance): void => {
    if (!instance) return
    setUserId(userId, instance)

    const filteredProperties: UTMParams = userProperties
        ? (Object.fromEntries(Object.entries(userProperties).filter(([, v]) => !Array.isArray(v))) as UTMParams)
        : {}

    const urlUTM = getUTMFromURL()
    if (hasProperties(urlUTM)) captureUTM(urlUTM, instance)
    if (hasProperties(filteredProperties)) sendSetOnceIdentify(filteredProperties, instance)

    try {
        identifyGAUser(userId, userProperties)
    } catch {
        /* GA must never break the app */
    }
}

/**
 * Resets Amplitude user session
 * Uses global instance by default, or pass explicit instance
 * Silently no-ops if no instance available
 */
export const setUserProperties = (
    properties: Record<string, PropertyValue>,
    instance: BrowserClient | undefined = globalInstance,
    options?: { onlyChanged?: boolean }
): void => {
    sendUserProperties(properties, instance, options)
}

export const setUserId = (userId: string, instance: BrowserClient | undefined = globalInstance): void => {
    if (!instance || !userId) return
    if (lastUserIdByInstance.get(instance) === userId) return

    instance.setUserId(userId)
    clearUserPropertiesCache(instance)
    lastUserIdByInstance.set(instance, userId)

    try {
        identifyGAUser(userId)
    } catch {
        /* GA must never break the app */
    }
}

export const reset = (instance: BrowserClient | undefined = globalInstance): void => {
    if (!instance) return
    lastIdentifySignatureByInstance.delete(instance)
    lastUserIdByInstance.delete(instance)
    clearUserPropertiesCache(instance)
    instance.reset()

    try {
        resetGAUser()
    } catch {
        /* GA must never break the app */
    }
}
